import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { Calendar, BarChart3, Tag, Wallet, TrendingDown, TrendingUp, Package, Landmark, FolderOpen, Inbox, Crown, CreditCard, Factory, Users, Wrench, Truck, FileText, ClipboardList, User, X, AlertTriangle, CheckCircle } from 'lucide-react';

const BusinessDashboard = () => {
  const routeBase = useWorkspaceBasePath();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('tout');
  const [activePeriodKey, setActivePeriodKey] = useState('tout');
  const [projectsSummary, setProjectsSummary] = useState([]);
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [customDateDebut, setCustomDateDebut] = useState('');
  const [customDateFin, setCustomDateFin] = useState('');

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, customDateDebut, customDateFin]);

  const loadDashboard = async () => {
    try {
      let url = `/business/dashboard?periode=${periode}`;
      if (periode === 'custom' && customDateDebut && customDateFin) {
        url += `&date_debut=${customDateDebut}&date_fin=${customDateFin}`;
      }
      const res = await api.get(url);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Charger les résumés financiers des projets
  const loadProjectsSummary = async () => {
    if (projectsSummary.length > 0) return; // Déjà chargé
    setLoadingProjects(true);
    try {
      let url = `/business/projects-summary?periode=${periode}`;
      if (periode === 'custom' && customDateDebut && customDateFin) {
        url += `&date_debut=${customDateDebut}&date_fin=${customDateFin}`;
      }
      const res = await api.get(url);
      setProjectsSummary(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fonction pour changer la période
  const handlePeriodeChange = (newPeriode) => {
    setActivePeriodKey(newPeriode);
    setProjectsSummary([]); // Reset pour recharger avec la nouvelle période

    // Pour les raccourcis qui n'existent pas côté API, on calcule les dates et on envoie en mode custom
    const now = new Date();
    if (newPeriode === 'mois_dernier') {
      const debut = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const fin = new Date(now.getFullYear(), now.getMonth(), 0);
      setCustomDateDebut(debut.toISOString().split('T')[0]);
      setCustomDateFin(fin.toISOString().split('T')[0]);
      setPeriode('custom');
      return;
    }
    if (newPeriode === '3mois') {
      const debut = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      setCustomDateDebut(debut.toISOString().split('T')[0]);
      setCustomDateFin(now.toISOString().split('T')[0]);
      setPeriode('custom');
      return;
    }
    if (newPeriode === '6mois') {
      const debut = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      setCustomDateDebut(debut.toISOString().split('T')[0]);
      setCustomDateFin(now.toISOString().split('T')[0]);
      setPeriode('custom');
      return;
    }

    // Pour les périodes natives de l'API
    if (newPeriode !== 'custom') {
      setCustomDateDebut('');
      setCustomDateFin('');
    }
    setPeriode(newPeriode);
  };

  // Charger les projets quand on déplie
  useEffect(() => {
    if (projectsExpanded) {
      loadProjectsSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsExpanded, periode]);

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const { stats, low_stock, restock_list, clients_with_credit, suppliers } = data || {};

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Tableau de bord</h1>
          <p className="text-gray-500 text-sm">Vue d'ensemble de votre activité</p>
        </div>
      </div>

      {/* Sélecteur de période global */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><Calendar size={16} /> Période</span>
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'tout', label: 'Tout' },
              { key: 'aujourdhui', label: "Aujourd'hui" },
              { key: 'semaine', label: 'Cette semaine' },
              { key: 'mois', label: 'Ce mois' },
              { key: 'mois_dernier', label: 'Mois dernier' },
              { key: '3mois', label: '3 derniers mois' },
              { key: '6mois', label: '6 derniers mois' },
              { key: 'annee', label: 'Cette année' },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => handlePeriodeChange(p.key)}
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition ${
                  activePeriodKey === p.key ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400">Personnalisé :</span>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Du</label>
            <input
              type="date"
              value={customDateDebut}
              onChange={(e) => { setCustomDateDebut(e.target.value); if (e.target.value && customDateFin) { setPeriode('custom'); setActivePeriodKey('custom'); } }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Au</label>
            <input
              type="date"
              value={customDateFin}
              onChange={(e) => { setCustomDateFin(e.target.value); if (customDateDebut && e.target.value) { setPeriode('custom'); setActivePeriodKey('custom'); } }}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          {(customDateDebut || customDateFin) && periode === 'custom' && (
            <button onClick={() => { setCustomDateDebut(''); setCustomDateFin(''); setPeriode('tout'); setActivePeriodKey('tout'); }} className="text-xs text-red-500 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition flex items-center gap-1"><X size={14} /> Effacer</button>
          )}
          {customDateDebut && customDateFin && periode === 'custom' && (
            <span className="text-xs text-gray-400 ml-auto">
              {new Date(customDateDebut).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })} → {new Date(customDateFin).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <BarChart3 size={24} />
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">CA</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">{stats?.chiffre_affaires?.toLocaleString()} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span></div>
          <div className="text-xs text-gray-500 mt-1">Chiffre d'affaires (vente)</div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <Tag size={24} />
            <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded-full">Coût</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">{stats?.cout_ventes?.toLocaleString()} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span></div>
          <div className="text-xs text-gray-500 mt-1">Coût des ventes</div>
          {/* Info achats stock */}
          {(stats?.achats_stock > 0 || stats?.cout_ventes > 0) && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Achats stock:</span>
                <span className="text-orange-600 font-medium">-{stats?.achats_stock?.toLocaleString() || 0} XOF</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-gray-500">Dispo. ravitaillement:</span>
                <span className={`font-medium ${(stats?.cout_ventes - (stats?.achats_stock || 0)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {((stats?.cout_ventes || 0) - (stats?.achats_stock || 0)).toLocaleString()} XOF
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <Wallet size={24} />
            <span className={`text-xs px-2 py-1 rounded-full ${stats?.marge_brute >= 0 ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'}`}>
              Marge
            </span>
          </div>
          <div className={`text-lg md:text-xl font-bold ${stats?.marge_brute >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {stats?.marge_brute?.toLocaleString()} <span className="text-xs md:text-sm font-normal">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Marge brute</div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <TrendingDown size={24} />
            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">Sorties</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">{stats?.total_sorties?.toLocaleString()} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span></div>
          <div className="text-xs text-gray-500 mt-1">Dépenses + Décaissements</div>
          {(stats?.depenses > 0 || stats?.decaissements > 0) && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Dépenses:</span>
                <span className="text-red-600 font-medium">{stats?.depenses?.toLocaleString() || 0} XOF</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-gray-500">Décaissements:</span>
                <span className="text-red-600 font-medium">{stats?.decaissements?.toLocaleString() || 0} XOF</span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <TrendingUp size={24} />
            <span className={`text-xs px-2 py-1 rounded-full ${stats?.benefice >= 0 ? 'text-emerald-600 bg-emerald-100' : 'text-red-600 bg-red-100'}`}>
              Bénéfice
            </span>
          </div>
          <div className={`text-lg md:text-xl font-bold ${stats?.benefice >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {stats?.benefice >= 0 ? '+' : ''}{stats?.benefice?.toLocaleString()} <span className="text-xs md:text-sm font-normal">XOF</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">Bénéfice net</div>
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <div className="flex items-center justify-between mb-3">
            <Package size={24} />
            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">Stock</span>
          </div>
          <div className="text-lg md:text-xl font-bold text-gray-800">{stats?.valeur_stock?.toLocaleString() || 0} <span className="text-xs md:text-sm font-normal text-gray-500">XOF</span></div>
          <div className="text-xs text-gray-500 mt-1">Valeur des stocks</div>
          {stats?.nb_produits > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Produits actifs:</span>
                <span className="text-purple-600 font-medium">{stats?.nb_produits}</span>
              </div>
              {stats?.stock_bas > 0 && (
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-gray-500">Stock bas:</span>
                  <span className="text-red-600 font-medium flex items-center gap-1"><AlertTriangle size={14} /> {stats?.stock_bas}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trésorerie */}
      <div className="grid grid-cols-1 gap-3 md:gap-4 mb-6">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow p-4 md:p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <Landmark size={24} />
            <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Caisse</span>
          </div>
          <div className="text-xl md:text-2xl font-bold">{stats?.tresorerie?.toLocaleString()} <span className="text-xs md:text-sm font-normal opacity-80">XOF</span></div>
          <div className="text-xs opacity-80 mt-1">Trésorerie disponible</div>
        </div>
      </div>

      {/* Section Finances des Projets (dépliable) */}
      <div className="bg-white rounded-xl shadow mb-6">
        <button
          onClick={() => setProjectsExpanded(!projectsExpanded)}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition rounded-xl"
        >
          <div className="flex items-center gap-3">
            <FolderOpen size={24} />
            <div className="text-left">
              <h3 className="font-semibold text-gray-800">Finances des projets</h3>
              <p className="text-xs text-gray-500">Résumé financier de vos projets</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {projectsSummary.length > 0 && (
              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">
                {projectsSummary.length} projet{projectsSummary.length > 1 ? 's' : ''}
              </span>
            )}
            <span className={`text-gray-400 transform transition-transform ${projectsExpanded ? 'rotate-180' : ''}`}>
              ▼
            </span>
          </div>
        </button>
        
        {projectsExpanded && (
          <div className="border-t px-4 pb-4">
            {loadingProjects ? (
              <div className="py-8 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-600"></div>
              </div>
            ) : projectsSummary.length === 0 ? (
              <div className="py-8 text-center">
                <Inbox size={40} className="mx-auto mb-2" />
                <p className="text-gray-500">Aucun projet avec des finances</p>
                <Link to="/projects" className="text-purple-600 text-sm hover:underline mt-2 inline-block">
                  Voir mes projets →
                </Link>
              </div>
            ) : (
              <div className="space-y-3 mt-4">
                {projectsSummary.map(project => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}/workspace`}
                    className="block p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800">{project.title}</span>
                        {project.is_owner ? (
                          <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full flex items-center gap-1"><Crown size={12} /> Mon projet</span>
                        ) : (
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">Collaborateur</span>
                        )}
                      </div>
                      <span className="text-gray-400">→</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div>
                        <div className="text-indigo-600 font-semibold">{project.stats?.tresorerie?.toLocaleString()} XOF</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1"><Landmark size={14} /> Caisse</div>
                      </div>
                      <div>
                        <div className={`font-semibold ${project.stats?.marge_brute >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {project.stats?.marge_brute?.toLocaleString()} XOF
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1"><Wallet size={14} /> Marge brute</div>
                      </div>
                      <div>
                        <div className="text-red-600 font-semibold">-{project.stats?.depenses?.toLocaleString()} XOF</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingDown size={14} /> Dépenses</div>
                      </div>
                      <div>
                        <div className={`font-semibold ${project.stats?.benefice >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {project.stats?.benefice >= 0 ? '+' : ''}{project.stats?.benefice?.toLocaleString()} XOF
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1"><TrendingUp size={14} /> Bénéfice net</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Second row stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-6">
        <Link to={`${routeBase}/invoices?statut=envoyee`} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
          <div className="text-amber-500 text-2xl mb-2"><FileText size={24} /></div>
          <div className="text-lg font-bold text-gray-800">{stats?.factures_impayees?.toLocaleString()} XOF</div>
          <div className="text-xs text-gray-500">Factures impayées</div>
        </Link>

        <Link to={`${routeBase}/clients?credit=true`} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
          <div className="text-orange-500 text-2xl mb-2"><CreditCard size={24} /></div>
          <div className="text-lg font-bold text-gray-800">{stats?.credits_clients?.toLocaleString()} XOF</div>
          <div className="text-xs text-gray-500">Crédits clients</div>
        </Link>

        <Link to={`${routeBase}/suppliers`} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
          <div className="text-red-500 text-2xl mb-2"><Factory size={24} /></div>
          <div className="text-lg font-bold text-gray-800">{stats?.credits_fournisseurs?.toLocaleString()} XOF</div>
          <div className="text-xs text-gray-500">Crédits fournisseurs</div>
        </Link>

        <Link to={`${routeBase}/products`} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
          <div className="text-purple-500 text-2xl mb-2"><Package size={24} /></div>
          <div className="text-lg font-bold text-gray-800">{stats?.nb_produits}</div>
          <div className="text-xs text-gray-500">Produits actifs</div>
        </Link>

        <Link to={`${routeBase}/clients`} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
          <div className="text-blue-500 text-2xl mb-2"><Users size={24} /></div>
          <div className="text-lg font-bold text-gray-800">{stats?.nb_clients}</div>
          <div className="text-xs text-gray-500">Clients</div>
        </Link>

        <Link to={`${routeBase}/prestations`} className="bg-white rounded-xl shadow p-3 md:p-4 hover:shadow-md transition">
          <div className="text-teal-500 text-xl md:text-2xl mb-2"><Wrench size={24} /></div>
          <div className="text-base md:text-lg font-bold text-gray-800">{stats?.prestations_en_cours}</div>
          <div className="text-xs text-gray-500">Prestations en cours</div>
        </Link>
      </div>

      {/* Alerts & Lists */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Ravitaillement à prévoir */}
        <div className="bg-white rounded-xl shadow">
          <Link to={`${routeBase}/restock`} className="p-4 border-b flex items-center justify-between hover:bg-gray-50 transition block">
            <h3 className="font-semibold text-gray-800 flex items-center gap-1"><Package size={16} /> Ravitaillement</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">
                {restock_list?.summary?.total_produits || 0} produit{(restock_list?.summary?.total_produits || 0) > 1 ? 's' : ''}
              </span>
              <span className="text-gray-400">→</span>
            </div>
          </Link>
          {restock_list?.products?.length > 0 && (
            <div className="px-4 py-2 bg-purple-50 border-b flex items-center justify-between text-xs">
              <span className="text-purple-700">
                <strong>{restock_list.summary.total_unites}</strong> unités à commander
              </span>
              <span className="text-purple-700 font-semibold">
                ~{restock_list.summary.cout_total?.toLocaleString()} XOF
              </span>
            </div>
          )}
          <div className="p-4 max-h-64 overflow-y-auto">
            {restock_list?.products?.length > 0 ? (
              <div className="space-y-3">
                {restock_list.products.slice(0, 8).map(product => (
                  <div key={product.id} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 text-sm truncate">{product.nom}</div>
                      <div className="text-xs text-gray-500">
                        Stock: {product.stock_actuel} / Seuil: {product.stock_minimum}
                      </div>
                    </div>
                    <div className="text-right ml-2">
                      <div className="text-purple-600 font-bold">+{product.quantite_a_commander}</div>
                      <div className="text-xs text-gray-500">{product.unite || 'unité(s)'}</div>
                    </div>
                  </div>
                ))}
                {restock_list.products.length > 8 && (
                  <Link to="/workspace/products?stock_bas=1" className="block text-center text-sm text-purple-600 hover:underline">
                    Voir tout ({restock_list.products.length} produits)
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4 flex items-center justify-center gap-1"><CheckCircle size={14} /> Stock suffisant</p>
            )}
          </div>
        </div>

        {/* Stock bas */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-1"><AlertTriangle size={14} /> Stock bas</h3>
            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">{low_stock?.length || 0}</span>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            {low_stock?.length > 0 ? (
              <div className="space-y-3">
                {low_stock.slice(0, 5).map(product => (
                  <div key={product.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-800 text-sm">{product.nom}</div>
                      <div className="text-xs text-gray-500">{product.categorie}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-red-600 font-bold">{product.stock_actuel}</div>
                      <div className="text-xs text-gray-500">min: {product.stock_minimum}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">Aucune alerte stock</p>
            )}
          </div>
        </div>

        {/* Clients avec crédit */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-1"><CreditCard size={16} /> Crédits clients</h3>
            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">{clients_with_credit?.length || 0}</span>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            {clients_with_credit?.length > 0 ? (
              <div className="space-y-3">
                {clients_with_credit.slice(0, 5).map(client => (
                  <div key={client.id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-800 text-sm">{client.nom}</div>
                      <div className="text-xs text-gray-500">{client.telephone}</div>
                    </div>
                    <div className="text-orange-600 font-bold">
                      {client.solde_credit?.toLocaleString()} XOF
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">Aucun crédit client</p>
            )}
          </div>
        </div>

        {/* Crédits fournisseurs */}
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800 flex items-center gap-1"><Factory size={16} /> Crédits fournisseurs</h3>
            <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">{suppliers?.filter(s => parseFloat(s.solde_credit) > 0).length || 0}</span>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto">
            {suppliers?.filter(s => parseFloat(s.solde_credit) > 0).length > 0 ? (
              <div className="space-y-3">
                {suppliers.filter(s => parseFloat(s.solde_credit) > 0).map(supplier => (
                  <Link key={supplier.id} to={`${routeBase}/suppliers`} className="flex items-center justify-between p-2 bg-red-50 rounded-lg hover:bg-red-100 transition">
                    <div>
                      <div className="font-medium text-gray-800 text-sm">{supplier.nom}</div>
                      <div className="text-xs text-gray-500">{supplier.telephone || 'Pas de téléphone'}</div>
                    </div>
                    <div className="text-red-600 font-bold">
                      {parseFloat(supplier.solde_credit).toLocaleString()} XOF
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-4">Aucun crédit fournisseur</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 bg-white rounded-xl shadow p-4">
        <h3 className="font-semibold text-gray-800 mb-4">Actions rapides</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
          <Link
            to="/workspace/invoices/new"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition"
          >
            <FileText size={24} />
            <span className="text-xs md:text-sm font-medium text-indigo-700 text-center">Facture</span>
          </Link>
          <Link
            to="/workspace/quotes/new"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition"
          >
            <ClipboardList size={24} />
            <span className="text-xs md:text-sm font-medium text-purple-700 text-center">Devis</span>
          </Link>
          <Link
            to="/workspace/payments/new"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-green-50 rounded-xl hover:bg-green-100 transition"
          >
            <Wallet size={24} />
            <span className="text-xs md:text-sm font-medium text-green-700 text-center">Paiement</span>
          </Link>
          <Link
            to="/workspace/expenses"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-red-50 rounded-xl hover:bg-red-100 transition"
          >
            <TrendingDown size={24} />
            <span className="text-xs md:text-sm font-medium text-red-700 text-center">Dépense</span>
          </Link>
          <Link
            to="/workspace/products"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-amber-50 rounded-xl hover:bg-amber-100 transition"
          >
            <Package size={24} />
            <span className="text-xs md:text-sm font-medium text-amber-700 text-center">Produit</span>
          </Link>
          <Link
            to="/workspace/clients"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition"
          >
            <User size={24} />
            <span className="text-xs md:text-sm font-medium text-blue-700 text-center">Client</span>
          </Link>
          <Link
            to="/workspace/restock"
            className="flex flex-col items-center gap-1 md:gap-2 p-2 md:p-4 bg-violet-50 rounded-xl hover:bg-violet-100 transition"
          >
            <Truck size={24} />
            <span className="text-xs md:text-sm font-medium text-violet-700 text-center">Ravitailler</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BusinessDashboard;
